// @flow
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { Loader2, Search } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomInput } from '@/components/custom-ui/custom-input.tsx';
import { Button } from '@/components/ui/button.tsx';

import { GetOneSaleInvoiceForRequest } from '../../api/saleToIncome.api';
import type { IInvoiceSaleFullCanEdit } from '../../api/saleToIncome.interface';

// Zod schema for form validation
const formSchema = z.object({
  invoiceID: z
    .number()
    .min(2000000, 'يجب أن يكون الرقم 2 مليون أو أكثر')
    .refine((value) => value.toString().length >= 7, {
      message: 'يجب أن يكون الرقم مكونًا من 7 أرقام على الأقل',
    }),
});

type FormData = z.infer<typeof formSchema>;

type Props = {
  setInvoiceData: (
    value: React.SetStateAction<IInvoiceSaleFullCanEdit | null>,
  ) => void;
  setLoading: (value: React.SetStateAction<boolean>) => void;
};

export function FormDialog({ setInvoiceData, setLoading }: Props) {
  const getItemsMutation = useMutation({
    mutationFn: GetOneSaleInvoiceForRequest,
    onSuccess: async (data) => {
      if (data?.data) {
        const updatedInvoiceData: IInvoiceSaleFullCanEdit = {
          invoiceHeader: data.data.invoiceHeader,
          oldData: data.data.oldData,
          invoiceItems: data.data.invoiceItems.map((item) => ({
            ...item,
            EditDiscount: item.Discount, // ✅ تعيين `EditDiscount` بنفس قيمة `DiscountIN`
          })),
        };
        setInvoiceData(updatedInvoiceData);
      }
      setLoading(false);
      toast.success('تم جلب الفاتورة بنجاح', {
        position: 'bottom-center',
      });
    },

    onError: (err: AxiosError<{ message?: string }>) => {
      setLoading(false);
      toast.error(err?.response?.data?.message || 'حدث خطأ ما', {
        position: 'top-right',
      });
    },
  });

  // React Hook Form setup
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      invoiceID: undefined,
    },
  });

  const onSubmit = async (values: FormData) => {
    setInvoiceData(null);
    setLoading(true);
    await getItemsMutation.mutateAsync({
      id: values.invoiceID,
    });
    form.reset();
  };

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = form;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="ml-16 flex gap-1.5">
        <div>
          <CustomInput
            label=""
            isInvalid={Boolean(errors.invoiceID)}
            errorMessage={errors.invoiceID?.message || ''}
            type="number"
            placeholder="رقم فاتورة البيع"
            id="days"
            className="text-center text-xs"
            classNameInput="w-52 text-sm h-9 border-indigo-200/70 dark:border-indigo-800/30 focus:border-indigo-400 dark:focus:border-indigo-600 focus:ring-1 focus:ring-indigo-400 dark:focus:ring-indigo-600"
            {...register('invoiceID', { valueAsNumber: true })}
            disabled={isSubmitting}
            required
          />
        </div>

        <div>
          <Button
            type="submit"
            disabled={isSubmitting}
            size="icon"
            className="h-9 w-9 bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 dark:from-indigo-600 dark:to-purple-600 dark:hover:from-indigo-500 dark:hover:to-purple-500"
          >
            {isSubmitting ? (
              <Loader2
                className="animate-spin text-white"
                size={16}
                aria-label="Loading Spinner"
                data-testid="loader"
              />
            ) : (
              <Search size={16} />
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
